import axios from "axios";

// Accept either `https://api.example.com` or a value with a trailing slash.
// Avoid generating `//api/...`, which some production proxies route as a
// different path and return 404 for authentication requests.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

let csrfRequest;
const sessionExpiredListeners = new Set();
export function onSessionExpired(listener) {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}
api.interceptors.request.use(async (config) => {
  if (!["get", "head", "options"].includes((config.method || "get").toLowerCase())) {
    if (!csrfRequest) {
      csrfRequest = axios.get(`${API}/auth/csrf`, { withCredentials: true })
        .then(({ data }) => data.csrf_token).catch((error) => { csrfRequest = null; throw error; });
    }
    config.headers["X-CSRF-Token"] = await csrfRequest;
  }
  return config;
});

api.interceptors.response.use((response) => {
  const config = response.config;
  if (config && !["get", "head", "options"].includes((config.method || "get").toLowerCase()) &&
      !(config.url || "").startsWith("/notifications")) {
    window.dispatchEvent(new Event("notification-activity"));
  }
  return response;
}, async (error) => {
  const config = error.config;
  // Authentication endpoints also use 401 for incorrect credentials. Those
  // failures belong to their forms, not to the current session.
  const path = (config?.url || "").split("?")[0];
  const credentialEndpoint = ["/auth/login", "/auth/register", "/auth/guest",
    "/auth/google", "/auth/google/link", "/auth/logout"].includes(path);
  if (error.response?.status === 401 && config &&
      !credentialEndpoint) {
    csrfRequest = null;
    sessionExpiredListeners.forEach((listener) => listener());
  }
  // The middleware rejects this request before running the endpoint, so one
  // retry cannot duplicate a successful write. Never retry other errors.
  if (config && !config._csrfRetried && error.response?.status === 403 &&
      error.response?.data?.detail === "Missing or invalid CSRF token") {
    csrfRequest = null;
    config._csrfRetried = true;
    return api(config);
  }
  return Promise.reject(error);
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function fileUrl(path) {
  // The API reads the configured private storage (local or R2) after checking
  // member/album permissions. Never expose bucket credentials or public URLs.
  return `${API}/files/${path.split("/").map(encodeURIComponent).join("/")}`;
}

export default api;
